import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import JobApplicationModal from '../../components/jobs/JobApplicationModal';
import RedactedValue from '../../components/RedactedValue';
import {
  MapPinIcon,
  CurrencyDollarIcon,
  ClockIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  StarIcon,
  EyeIcon,
  UserGroupIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  CheckIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface Job {
  id: string;
  title: string;
  description?: string;
  location?: string;
  remoteType?: 'onsite' | 'remote' | 'hybrid';
  jobType?: 'full-time' | 'part-time' | 'contract' | 'internship';
  experienceLevel?: 'entry' | 'mid' | 'senior' | 'executive';
  salaryRange?: {
    min: number;
    max: number;
    currency: string;
  };
  skillsRequired?: string[];
  requirements?: string[];
  benefits?: string[];
  status?: 'active' | 'paused' | 'closed' | 'draft';
  createdAt: string | { _seconds: number; _nanoseconds: number };
  updatedAt: string | { _seconds: number; _nanoseconds: number };
  employerId: string;
  employerName: string;
  applicationsCount?: number;
  /** Set by the API when the count was withheld (signed-out viewer). */
  applicationsCountLocked?: boolean;
  viewsCount?: number;
  applicationDeadline?: string;
  isActive: boolean;
  isFeatured?: boolean;
  autoOffer?: {
    enabled: boolean;
    timeLimit: number;
    requiredCourses: Array<{
      courseId: string;
      courseName: string;
      minimumGrade: number;
    }>;
    minimumOverallScore?: number;
    createdAt: Date;
  };
  autoOfferDeadline?: string | Date;
  autoOfferEnabled?: boolean;
  autoOfferMinimumScore?: number;
  autoOfferRequiredCourses?: Array<{
    courseId: string;
    courseName: string;
    minimumGrade: number;
  }>;
}

// Helper function to format Firebase timestamps
const formatFirebaseDate = (timestamp: string | { _seconds: number; _nanoseconds: number }) => {
  if (typeof timestamp === 'string') {
    return new Date(timestamp).toLocaleDateString();
  } else if (timestamp && typeof timestamp === 'object' && '_seconds' in timestamp) {
    return new Date(timestamp._seconds * 1000).toLocaleDateString();
  }
  return 'Invalid Date';
};

const formatJobType = (type: string | undefined) => {
  if (!type) return 'Not specified';
  return type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ');
};

const formatExperienceLevel = (level: string | undefined) => {
  if (!level) return 'Not specified';
  switch (level) {
    case 'entry': return 'Entry Level';
    case 'mid': return 'Mid Level';
    case 'senior': return 'Senior Level';
    case 'executive': return 'Executive';
    default: return level;
  }
};

const formatSalary = (salaryRange?: { min: number; max: number; currency: string }) => {
  if (!salaryRange || !salaryRange.min || !salaryRange.max) {
    return 'Salary not specified';
  }

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: salaryRange.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return `${formatAmount(salaryRange.min)} - ${formatAmount(salaryRange.max)}`;
};

// Function to safely render HTML content
const createSafeHTML = (htmlString: string) => {
  const cleanHTML = DOMPurify.sanitize(htmlString, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'div', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'class'],
    ALLOWED_URI_REGEXP: /^https?:\/\//
  });
  return { __html: cleanHTML };
};

const JobDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [employerProfile, setEmployerProfile] = useState<any>(null);
  const [loadingEmployer, setLoadingEmployer] = useState(false);
  const [autoOfferStatus, setAutoOfferStatus] = useState<any>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  useEffect(() => {
    if (id) {
      fetchJobDetails(id);
      fetchAutoOfferStatus(id);
    }
  }, [id]);

  // Update timer every minute
  useEffect(() => {
    if (autoOfferStatus?.deadline) {
      const updateTimer = () => {
        const deadline = new Date(autoOfferStatus.deadline);
        const now = new Date();
        const timeDiff = deadline.getTime() - now.getTime();
        
        if (timeDiff <= 0) {
          setTimeRemaining('Auto-offer period ended');
          return;
        }
        
        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) {
          setTimeRemaining(`${days}d ${hours}h ${minutes}m remaining`);
        } else if (hours > 0) {
          setTimeRemaining(`${hours}h ${minutes}m remaining`);
        } else {
          setTimeRemaining(`${minutes}m remaining`);
        }
      };
      
      updateTimer();
      const interval = setInterval(updateTimer, 60000); // Update every minute
      
      return () => clearInterval(interval);
    }
  }, [autoOfferStatus]);

  useEffect(() => {
    if (job && job.employerId) {
      fetchEmployerProfile(job.employerId);
    }
  }, [job]);

  const fetchJobDetails = async (jobId: string) => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setJob(result.data.job);
        // Set application status from backend response
        if (result.data.hasApplied !== undefined) {
          setHasApplied(result.data.hasApplied);
        }
      } else {
        throw new Error(result.message || 'Failed to fetch job details');
      }
    } catch (error: any) {
      console.error('Error fetching job details:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployerProfile = async (employerId: string) => {
    try {
      setLoadingEmployer(true);
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/profiles/employer/${employerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setEmployerProfile(result.data);
        }
      }
      // Don't show error toast for employer profile - it's supplementary info
    } catch (error: any) {
      console.error('Error fetching employer profile:', error);
      // Don't show error toast for employer profile - it's supplementary info
    } finally {
      setLoadingEmployer(false);
    }
  };

  const fetchAutoOfferStatus = async (jobId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/${jobId}/auto-offer-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data.autoOfferStatus) {
          setAutoOfferStatus(result.data.autoOfferStatus);
        }
      }
    } catch (error: any) {
      console.error('Error fetching auto-offer status:', error);
      // Don't show error toast for auto-offer status - it's supplementary info
    }
  };

  const handleApply = () => {
    if (!userProfile) {
      toast.error('Please log in to apply for jobs');
      return;
    }

    if (userProfile.userType !== 'student') {
      toast.error('Only students can apply for jobs');
      return;
    }

    setShowApplicationModal(true);
  };

  const handleApplicationSuccess = () => {
    setHasApplied(true);
    setShowApplicationModal(false);
    // Refresh job data to get updated application count
    if (id) {
      fetchJobDetails(id);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h2>
          <p className="text-gray-600 mb-6">The job you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/jobs')}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/jobs')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Jobs
          </button>
        </div>

        {/* Job Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">{job.title}</h1>
                {job.isFeatured && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <StarIcon className="h-3 w-3 mr-1" />
                    Featured
                  </span>
                )}
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-gray-600 mb-4">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="truncate">{job.employerName}</span>
                </div>
                <div className="flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="truncate">{job.location || 'Location not specified'} • {formatJobType(job.remoteType)}</span>
                </div>
                <div className="flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="whitespace-nowrap">Posted {formatFirebaseDate(job.createdAt)}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm text-gray-500">
                <div className="flex items-center">
                  <EyeIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="whitespace-nowrap">{job.viewsCount || 0} views</span>
                </div>
                <div className="flex items-center">
                  <UserGroupIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                  {/* The API omits applicationsCount entirely when signed out, so
                      there is no real number here to reveal. */}
                  {job.applicationsCountLocked ? (
                    <RedactedValue bars={2} label="applicants" />
                  ) : (
                    <span className="whitespace-nowrap">{job.applicationsCount || 0} applicants</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col lg:items-end gap-3 sm:justify-between lg:justify-start">
              {job.salaryRange && job.salaryRange.min && job.salaryRange.max ? (
                <div className="text-right">
                  <div className="flex items-center text-green-600 font-semibold text-lg">
                    <CurrencyDollarIcon className="h-5 w-5 mr-1" />
                    {formatSalary(job.salaryRange)}
                  </div>
                  <div className="text-sm text-gray-500">per year</div>
                </div>
              ) : (
                <div className="text-right">
                  <div className="flex items-center text-gray-500 font-semibold text-lg">
                    <CurrencyDollarIcon className="h-5 w-5 mr-1" />
                    Salary not specified
                  </div>
                </div>
              )}
              
              {userProfile?.userType === 'student' && (
                <button
                  onClick={handleApply}
                  disabled={hasApplied}
                  className={`px-6 py-3 rounded-md font-medium ${
                    hasApplied
                      ? 'bg-green-100 text-green-800 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  {hasApplied ? (
                    <>
                      <CheckIcon className="h-4 w-4 inline mr-2" />
                      Applied
                    </>
                  ) : (
                    'Apply Now'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Auto-Offer Status - Moved up and made horizontal */}
        {(job.autoOffer?.enabled || job.autoOfferEnabled || autoOfferStatus?.enabled) && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                  <h2 className="text-xl font-bold text-green-800">⚡ Auto-Offer Active</h2>
                </div>
                <span className="px-3 py-1 bg-green-200 text-green-800 rounded-full text-sm font-bold animate-pulse">
                  FAST TRACK
                </span>
              </div>
              <div className="flex items-center gap-2 bg-white bg-opacity-70 px-4 py-2 rounded-full">
                <ClockIcon className="h-5 w-5 text-green-600" />
                <span className="text-lg font-bold text-green-700">
                  {timeRemaining || (() => {
                    if (job.autoOfferDeadline) {
                      let deadline: Date;
                      
                      // Handle Firebase timestamp format
                      if (typeof job.autoOfferDeadline === 'object' && job.autoOfferDeadline && 'seconds' in job.autoOfferDeadline) {
                        deadline = new Date((job.autoOfferDeadline as any).seconds * 1000);
                      } else if (typeof job.autoOfferDeadline === 'object' && job.autoOfferDeadline && '_seconds' in job.autoOfferDeadline) {
                        deadline = new Date((job.autoOfferDeadline as any)._seconds * 1000);
                      } else if (typeof job.autoOfferDeadline === 'object' && job.autoOfferDeadline && 'toDate' in job.autoOfferDeadline) {
                        deadline = (job.autoOfferDeadline as any).toDate();
                      } else {
                        deadline = new Date(job.autoOfferDeadline as string | Date);
                      }
                      
                      if (isNaN(deadline.getTime())) return 'Invalid deadline';
                      
                      const now = new Date();
                      const timeDiff = deadline.getTime() - now.getTime();
                      
                      if (isNaN(timeDiff)) return 'Calculating...';
                      
                      const hoursLeft = Math.max(0, Math.floor(timeDiff / (1000 * 60 * 60)));
                      const minutesLeft = Math.max(0, Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60)));
                      
                      if (timeDiff <= 0) return 'Auto-offer ended';
                      if (hoursLeft > 24) {
                        const days = Math.floor(hoursLeft / 24);
                        return `${days}d ${hoursLeft % 24}h left`;
                      } else if (hoursLeft > 0) {
                        return `${hoursLeft}h ${minutesLeft}m left`;
                      } else {
                        return `${minutesLeft}m left`;
                      }
                    }
                    return 'Calculating...';
                  })()}
                </span>
              </div>
            </div>
            
            <p className="text-green-700 mb-4 font-medium">
              🚀 The top qualifying candidate will be automatically approved when the timer expires!
            </p>
            
            {/* Horizontal layout for auto-offer details */}
            <div className="grid grid-cols-1">
              {/* Required Courses */}
              {((job.autoOffer?.requiredCourses || job.autoOfferRequiredCourses || autoOfferStatus?.requiredCourses) && 
                (job.autoOffer?.requiredCourses || job.autoOfferRequiredCourses || autoOfferStatus?.requiredCourses)!.length > 0) && (
                <div className="bg-white bg-opacity-70 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AcademicCapIcon className="h-5 w-5 text-green-600" />
                    <span className="font-bold text-green-800">Required Courses</span>
                  </div>
                  <div className="space-y-2">
                    {(job.autoOffer?.requiredCourses || job.autoOfferRequiredCourses || autoOfferStatus?.requiredCourses)!.slice(0, 3).map((course: any, index: number) => (
                      <div key={index} className="flex items-center justify-between text-sm bg-green-50 px-3 py-2 rounded">
                        <span className="font-medium text-green-800">{course.courseName}</span>
                        <span className="font-bold text-green-600">{course.minimumGrade}%+</span>
                      </div>
                    ))}
                    {(job.autoOffer?.requiredCourses || job.autoOfferRequiredCourses || autoOfferStatus?.requiredCourses)!.length > 3 && (
                      <div className="text-sm text-green-600 font-medium text-center">
                        +{(job.autoOffer?.requiredCourses || job.autoOfferRequiredCourses || autoOfferStatus?.requiredCourses)!.length - 3} more courses
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Minimum Score
              {(job.autoOffer?.minimumOverallScore || job.autoOfferMinimumScore || autoOfferStatus?.minimumScore) && (
                <div className="bg-white bg-opacity-70 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <StarIcon className="h-5 w-5 text-green-600" />
                    <span className="font-bold text-green-800">Min. Score Required</span>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">
                      {job.autoOffer?.minimumOverallScore || job.autoOfferMinimumScore || autoOfferStatus?.minimumScore}%
                    </div>
                  </div>
                </div>
              )}
              
              {/* User Eligibility */}
              {autoOfferStatus?.userEligibility && (
                <div className="bg-white bg-opacity-70 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    {autoOfferStatus.userEligibility.eligible ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    ) : (
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                    )}
                    <span className="font-bold text-green-800">
                      {autoOfferStatus.userEligibility.eligible ? "You're Eligible!" : "Not Yet Eligible"}
                    </span>
                  </div>
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${
                      autoOfferStatus.userEligibility.score >= (autoOfferStatus.minimumScore || 80) 
                        ? 'text-green-600' 
                        : 'text-yellow-600'
                    }`}>
                      {autoOfferStatus.userEligibility.score}%
                    </div>
                    <div className="text-sm text-green-700">Your Score</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 lg:space-y-6">
            {/* Job Description */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Job Description</h2>
              <div className="prose prose-gray max-w-none">
                {job.description?.includes('<') && job.description?.includes('>') ? (
                  // Render as HTML if it contains HTML tags
                  <div 
                    className="text-gray-700" 
                    dangerouslySetInnerHTML={createSafeHTML(job.description)}
                  />
                ) : (
                  // Render as plain text with line breaks preserved
                  <p className="text-gray-700 whitespace-pre-line">{job.description}</p>
                )}
              </div>
            </div>

            {/* Requirements */}
            {job.requirements && job.requirements.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Requirements</h2>
                <ul className="space-y-2">
                  {job.requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Benefits */}
            {job.benefits && job.benefits.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Benefits</h2>
                <ul className="space-y-2">
                  {job.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <StarIcon className="h-5 w-5 text-indigo-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4 lg:space-y-6">
            {/* Job Info */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Information</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <BriefcaseIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Job Type</div>
                    <div className="text-sm text-gray-600">{formatJobType(job.jobType)}</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <AcademicCapIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Experience Level</div>
                    <div className="text-sm text-gray-600">{formatExperienceLevel(job.experienceLevel)}</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Work Type</div>
                    <div className="text-sm text-gray-600">{formatJobType(job.remoteType)}</div>
                  </div>
                </div>
              </div>
            </div>


            {/* Required Skills */}
            {job.skillsRequired && job.skillsRequired.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Required Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {job.skillsRequired.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 whitespace-nowrap"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Company Info */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">About the Company</h3>
              <div className="flex items-start">
                <BuildingOfficeIcon className="h-6 w-6 text-gray-400 mr-3 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 mb-2">{job.employerName}</div>
                  
                  {loadingEmployer ? (
                    <div className="text-sm text-gray-500">Loading company information...</div>
                  ) : employerProfile ? (
                    <div className="space-y-3">
                      {/* Company Description - handle both formats */}
                      <div className="text-sm text-gray-600">
                        {employerProfile.companyDetails?.companyDescription || 
                         employerProfile.description || 
                         `${job.employerName} is actively hiring for various positions.`}
                      </div>
                      
                      {/* Mission Statement - handle both formats */}
                      {((employerProfile.companyDetails?.missionStatement && 
                         employerProfile.companyDetails.missionStatement !== `${job.employerName} is committed to innovation and excellence.`) ||
                        (employerProfile.missionStatement && 
                         employerProfile.missionStatement !== `${job.employerName} is committed to innovation and excellence.`)) && (
                        <div>
                          <div className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-1">Mission</div>
                          <div className="text-sm text-gray-600">
                            {employerProfile.companyDetails?.missionStatement || employerProfile.missionStatement}
                          </div>
                        </div>
                      )}
                      
                      {/* Company Stats - handle both formats */}
                      <div className="flex flex-wrap gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                        {(employerProfile.companyDetails?.employeeCount || employerProfile.companySize) && (
                          <div className="flex items-center gap-1">
                            <UserGroupIcon className="h-3 w-3" />
                            {employerProfile.companyDetails?.employeeCount 
                              ? `${employerProfile.companyDetails.employeeCount}+ employees`
                              : employerProfile.companySize}
                          </div>
                        )}
                        {(employerProfile.companyDetails?.foundedYear || employerProfile.foundedYear) && (
                          <div className="flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" />
                            Founded {employerProfile.companyDetails?.foundedYear || employerProfile.foundedYear}
                          </div>
                        )}
                        {employerProfile.website && (
                          <div className="flex items-center gap-1">
                            <BuildingOfficeIcon className="h-3 w-3" />
                            <a 
                              href={employerProfile.website.startsWith('http') ? employerProfile.website : `https://${employerProfile.website}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-800"
                            >
                              Website
                            </a>
                          </div>
                        )}
                        {employerProfile.industry && (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-600">{employerProfile.industry}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Company Values - handle both formats */}
                      {((employerProfile.companyDetails?.values && employerProfile.companyDetails.values.length > 0) ||
                        (employerProfile.companyValues && employerProfile.companyValues.length > 0)) && (
                        <div>
                          <div className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-1">Values</div>
                          <div className="flex flex-wrap gap-1">
                            {(employerProfile.companyDetails?.values || employerProfile.companyValues || []).map((value: string, index: number) => (
                              <span 
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {value}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      {job.employerName} is actively hiring for various positions. Visit their website or apply to learn more about the company culture and opportunities.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Application Modal */}
      {job && (
        <JobApplicationModal
          job={{
            ...job,
            description: job.description || 'No description available',
            location: job.location || 'Location not specified',
            remoteType: job.remoteType || 'onsite',
            jobType: job.jobType || 'full-time',
            experienceLevel: job.experienceLevel || 'mid',
            salaryRange: job.salaryRange || { min: 0, max: 0, currency: 'USD' },
            applicationsCount: job.applicationsCount || 0,
            viewsCount: job.viewsCount || 0,
            requirements: job.requirements || [],
            skills: job.skillsRequired || [] // Adapt skillsRequired to skills for modal
          }}
          isOpen={showApplicationModal}
          onClose={() => setShowApplicationModal(false)}
          onSuccess={handleApplicationSuccess}
        />
      )}
    </div>
  );
};

export default JobDetailsPage; 